import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import { AttachmentsService, DownloadService, FolderService, WorkspaceService } from '@pt/api-client';

import * as WorkspaceActions from '../actions/workspace.actions';
import { downloadWorkspaceItemEnd } from '../actions/workspace.actions';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { forkJoin, from, of } from 'rxjs';
import {
  Folder,
  WorkspaceItem,
  WorkspaceItemType,
  WorkspaceMoveRequest,
  WorkspaceParentItem
} from '@primatoo-ms-organization';
import { ColumnWithLocalization } from 'it/primatoo/openapi/api/org/schemas/common/ColumnWithLocalization';
import {
  DownloadResponse,
  NavigationData,
  PtUiAppRoutes,
  TreatmentTypesConverter,
  UiDocumentItem,
  WorkspaceRenameRequest
} from '@pt/pt-models';
import { Store } from '@ngrx/store';
import { DocumentItemUtils, PtCoreActions, PtCoreSelectors } from '@pt/pt-core';
import {
  AiAssessmentActions,
  AppAssessmentActions,
  AttachFileDialogComponent,
  AttachmentActions,
  CovidAssessmentActions,
  DeleteService,
  DialogService,
  FolderActions,
  RiskAssessmentActions,
  TreatmentActions,
  WidgetDialogService
} from '@pt/pt-shared';
import { AttachFileDialog, CreateRiskAssessmentDialogDto, DocumentsDialogService } from '../../../../../../shared';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { LoginResponse } from 'it/primatoo/openapi/api/adm/schemas/login/LoginResponse';
import { Organization } from 'it/primatoo/openapi/api/org/schemas/organization/Organization';

@Injectable()
export class WorkspaceEffects {

  constructor(
    private modalService: NgbModal,
    private deleteService: DeleteService,
    private actions$: Actions,
    private translate: TranslateService,
    private workspaceService: WorkspaceService,
    private folderService: FolderService,
    private dialogService: DialogService,
    private attachmentsService: AttachmentsService,
    private documentsDialogService: DocumentsDialogService,
    private downloadService: DownloadService,
    private widgetDialogService: WidgetDialogService,
    private store: Store
  ) {
  }

  loadResource$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        WorkspaceActions.loadWorkspaceItems
      ),
      switchMap((action: { parentId?: number }) => {
        if (Number.isSafeInteger(action.parentId)) {
          return of(WorkspaceActions.loadWorkspaceFolderChildren({ parentId: action.parentId as number }));
        } else {
          return of(WorkspaceActions.loadWorkspaceRoot());
        }
      })
    );
  });

  loadResourceRoot$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(WorkspaceActions.loadWorkspaceRoot),
      switchMap(() =>
        this.workspaceService.getWorkspaceRoot().pipe(
          map((resource: WorkspaceItem) =>
            WorkspaceActions.loadWorkspaceFolderChildren({ parentId: resource.id })
          ),
          catchError((error) => of(WorkspaceActions.loadWorkspaceItemsFailed({ error })))
        ))
    );
  });

  loadResourceChildren$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(WorkspaceActions.loadWorkspaceFolderChildren),
      switchMap((action: { parentId: number }) => {
        return forkJoin({
          items: this.workspaceService.getFolderChildren(action.parentId),
          parentItems: this.workspaceService.workspaceItemParents(action.parentId)
        }).pipe(
          map((response: { items: WorkspaceItem[], parentItems: WorkspaceParentItem[] }) =>
            WorkspaceActions.loadWorkspaceItemsSuccess({
              items: response.items,
              parentItems: response.parentItems
            })),
          catchError((error) => of(WorkspaceActions.loadWorkspaceItemsFailed({ error })))
        );
      })
    );
  });

  searchResource$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(WorkspaceActions.searchWorkspaceItems),
      switchMap((action: { name: string }) =>
        this.workspaceService.workspaceSearch(action.name).pipe(
          map((resources: WorkspaceItem[]) => WorkspaceActions.searchWorkspaceItemsSuccess({ items: resources })),
          catchError((error) => of(WorkspaceActions.searchWorkspaceItemsFailed({ error })))
        ))
    );
  });

  getResourcesColEnabled$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(WorkspaceActions.getWorkspaceColEnabled),
      switchMap(() => {
        return this.workspaceService.getWorkspaceColEnabled().pipe(
          map((colEnabled: ColumnWithLocalization[]) => WorkspaceActions.getWorkspaceColEnabledSuccess({ colEnabled })),
          catchError(err => of(WorkspaceActions.getWorkspaceColEnabledFailed({ error: err.message })))
        );
      })
    );
  });

  navigateToWorkspaceSearch$ = createEffect(() => this.actions$.pipe(
    ofType(WorkspaceActions.navigateToWorkspaceSearch),
    tap((action: { name: string, onBlankPage?: boolean }) => {
      this.store.dispatch(PtCoreActions.navigateTo({
        route: [PtUiAppRoutes.WORKSPACE, PtUiAppRoutes.SEARCH],
        queryParams: { name: action.name },
        onBlankPage: action.onBlankPage
      }));
    })
  ), { dispatch: false });

  moveResourceDialog$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(WorkspaceActions.moveWorkspaceItemDialog),
      switchMap((action: { item: WorkspaceItem }) => {
        return this.folderService.findAllFolders().pipe(
          map((folders: Folder[]) => WorkspaceActions.moveWorkspaceItemDialogWithFolder({ ...action, folders })),
          catchError(() => of(WorkspaceActions.moveWorkspaceItemDialogWithFolder({ ...action, folders: [] }))));
      })
    );
  });

  moveResourceDialogWithFolder$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(WorkspaceActions.moveWorkspaceItemDialogWithFolder),
      map((action: { item: WorkspaceItem, folders: Folder[] }) => {
          const resource = action.item;
          const title = resource?.type ? this.translate.stream('LABELS.MOVE_' + resource.type) : this.translate.stream('LABELS.MOVE');
          const folders: NavigationData[] = action?.folders?.map(l => NavigationData.folderToNavigationData(l));
          this.dialogService.runMoveItemDialog(
            title,
            resource,
            folders
          ).subscribe((response: { parentId: number }) => {
            if (response) {
              const targetFolderId = response?.parentId;
              this.store.dispatch(WorkspaceActions.moveWorkspaceItem({ item: action.item, targetFolderId }));
            }
          });
        }
      )
    );
  }, { dispatch: false });

  moveResource$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(WorkspaceActions.moveWorkspaceItem),
      tap((action: { item: WorkspaceItem, targetFolderId: number }) => {

        const id: number = action.item?.id;
        const workspaceMoveRequest: WorkspaceMoveRequest = { parentId: action?.targetFolderId !== undefined ? action.targetFolderId : null };
        const workspaceItemType: WorkspaceItemType = action.item.type as WorkspaceItemType;

        switch (workspaceItemType) {
          case WorkspaceItemType.AI_ASSESSMENT:
          case WorkspaceItemType.AI_ASSESSMENT_VALIDATED:
            this.store.dispatch(AiAssessmentActions.moveAiAssessment({ id, workspaceMoveRequest }));
            break;
          case WorkspaceItemType.APP_ASSESSMENT:
          case WorkspaceItemType.APP_ASSESSMENT_VALIDATED:
            this.store.dispatch(AppAssessmentActions.moveAppAssessment({ id, workspaceMoveRequest }));
            break;
          case WorkspaceItemType.COVID_ASSESSMENT:
          case WorkspaceItemType.COVID_ASSESSMENT_VALIDATED:
            this.store.dispatch(CovidAssessmentActions.moveCovidAssessment({ id, workspaceMoveRequest }));
            break;
          case WorkspaceItemType.FOLDER:
            this.store.dispatch(FolderActions.moveFolder({ id, workspaceMoveRequest }));
            break;
          case WorkspaceItemType.GLOBAL_MEASURE_CHECK:
          case WorkspaceItemType.GLOBAL_MEASURE_CHECK_VALIDATED:
            this.store.dispatch(RiskAssessmentActions.moveRiskAssessment({ id, workspaceMoveRequest }));
            break;
          case WorkspaceItemType.ATTACHMENT:
            this.store.dispatch(AttachmentActions.moveAttachment({ id, workspaceMoveRequest }));
            break;
          case WorkspaceItemType.TREATMENT_RESPONSIBLE:
          case WorkspaceItemType.TREATMENT_HOLDER:
          case WorkspaceItemType.TREATMENT_PROJECT:
          case WorkspaceItemType.TREATMENT_SUB_RESPONSIBLE:
          case WorkspaceItemType.TREATMENT_PROJECT_VALIDATED:
          case WorkspaceItemType.TREATMENT_HOLDER_VALIDATED:
          case WorkspaceItemType.TREATMENT_SUB_RESPONSIBLE_VALIDATED:
          case WorkspaceItemType.TREATMENT_RESPONSIBLE_VALIDATED:
            this.store.dispatch(TreatmentActions.moveTreatment({ id, workspaceMoveRequest, workspaceItemType }));
            break;
        }
      })
    );
  }, { dispatch: false });

  deleteResource$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(WorkspaceActions.deleteWorkspaceItem),
      tap((action: { item: WorkspaceItem }) => {
        const docDeleteItem: UiDocumentItem = DocumentItemUtils.fromWorkspaceItem(action.item);
        switch (action.item.type) {
          case WorkspaceItemType.AI_ASSESSMENT:
          case WorkspaceItemType.AI_ASSESSMENT_VALIDATED:
            this.deleteService.deleteDocument('aiAssessment', [docDeleteItem]).subscribe(
              (success) => {
                if (success) {
                  this.store.dispatch(AiAssessmentActions.deleteAiAssessmentSuccess());
                }
              }
            );
            break;
          case WorkspaceItemType.APP_ASSESSMENT:
          case WorkspaceItemType.APP_ASSESSMENT_VALIDATED:
            this.deleteService.deleteDocument('appAssessment', [docDeleteItem]).subscribe(
              (success) => {
                if (success) {
                  this.store.dispatch(AppAssessmentActions.deleteAppAssessmentSuccess());
                }
              }
            );
            break;
          case WorkspaceItemType.COVID_ASSESSMENT:
          case WorkspaceItemType.COVID_ASSESSMENT_VALIDATED:
            this.deleteService.deleteDocument('covidAssessment', [docDeleteItem]).subscribe(
              (success) => {
                if (success) {
                  this.store.dispatch(CovidAssessmentActions.deleteCovidAssessmentSuccess());
                }
              }
            );
            break;
          case WorkspaceItemType.FOLDER:
            this.deleteService.deleteEntity('folder', [action.item]).subscribe(
              (success) => {
                if (success) {
                  this.store.dispatch(FolderActions.deleteFolderSuccess());
                }
              });
            break;
          case WorkspaceItemType.GLOBAL_MEASURE_CHECK:
          case WorkspaceItemType.GLOBAL_MEASURE_CHECK_VALIDATED:
            this.deleteService.deleteDocument('globalMeasureCheck', [docDeleteItem]).subscribe(
              (success) => {
                if (success) {
                  this.store.dispatch(RiskAssessmentActions.deleteRiskAssessmentSuccess());
                }
              }
            );
            break;
          case WorkspaceItemType.ATTACHMENT:
            this.deleteService.deleteEntity('attachment', [action.item]).subscribe(
              (success) => {
                if (success) {
                  this.store.dispatch(AttachmentActions.deleteAttachmentSuccess());
                }
              }
            );
            break;

          case WorkspaceItemType.TREATMENT_HOLDER:
          case WorkspaceItemType.TREATMENT_HOLDER_VALIDATED:
            this.deleteService.deleteDocument('treatmentsHolder', [docDeleteItem]).subscribe(
              (success) => {
                if (success) {
                  this.store.dispatch(TreatmentActions.deleteTreatmentSuccess());
                }
              }
            );
            break;
          case WorkspaceItemType.TREATMENT_RESPONSIBLE:
          case WorkspaceItemType.TREATMENT_RESPONSIBLE_VALIDATED:
            this.deleteService.deleteDocument('treatmentsResponsible', [docDeleteItem]).subscribe(
              (success) => {
                if (success) {
                  this.store.dispatch(TreatmentActions.deleteTreatmentSuccess());
                }
              }
            );
            break;
          case WorkspaceItemType.TREATMENT_SUB_RESPONSIBLE:
          case WorkspaceItemType.TREATMENT_SUB_RESPONSIBLE_VALIDATED:
            this.deleteService.deleteDocument('treatmentsSubResponsible', [docDeleteItem]).subscribe(
              (success) => {
                if (success) {
                  this.store.dispatch(TreatmentActions.deleteTreatmentSuccess());
                }
              }
            );
            break;
          case WorkspaceItemType.TREATMENT_PROJECT:
          case WorkspaceItemType.TREATMENT_PROJECT_VALIDATED:
            this.deleteService.deleteDocument('projectCards', [docDeleteItem]).subscribe(
              (success) => {
                if (success) {
                  this.store.dispatch(TreatmentActions.deleteTreatmentSuccess());
                }
              }
            );
            break;

          default:
            break;
        }

      })
    );
  }, { dispatch: false });

  openWorkspaceItem$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(WorkspaceActions.openWorkspaceItem),
      tap((action: { item: WorkspaceItem, selected?: number }) => {
        let route: string[] = [];
        let queryParams = {};
        let openBlank = false;
        if (action?.selected !== null && action?.selected !== undefined) {
          queryParams = { ...queryParams, id: action.selected.toString(), opened: true };
        }

        const noSelected: boolean = action?.selected === null || action?.selected === undefined;
        const existItemId: boolean = action.item.id !== null && action.item.id !== undefined;

        // add edit for documents
        // add condition for validated documents
        // viewer/id?version=versionId
        let isValidated = false;
        switch (action.item.type) {
          case WorkspaceItemType.AI_ASSESSMENT_VALIDATED:
          case WorkspaceItemType.APP_ASSESSMENT_VALIDATED:
          case WorkspaceItemType.COVID_ASSESSMENT_VALIDATED:
          case WorkspaceItemType.GLOBAL_MEASURE_CHECK_VALIDATED:
          case WorkspaceItemType.TREATMENT_RESPONSIBLE_VALIDATED:
          case WorkspaceItemType.TREATMENT_PROJECT_VALIDATED:
          case WorkspaceItemType.TREATMENT_HOLDER_VALIDATED:
          case WorkspaceItemType.TREATMENT_SUB_RESPONSIBLE_VALIDATED:
            isValidated = true;
        }

        switch (action.item.type) {
          case WorkspaceItemType.AI_ASSESSMENT:
          case WorkspaceItemType.AI_ASSESSMENT_VALIDATED:
            if (noSelected && existItemId) {
              if (isValidated) {
                route.push(...[PtUiAppRoutes.BUSINESS_MANAGEMENT, PtUiAppRoutes.AI_ASSESSMENTS, action.item.id?.toString()]);
              } else {
                route.push(...[PtUiAppRoutes.BUSINESS_MANAGEMENT, PtUiAppRoutes.AI_ASSESSMENTS, PtUiAppRoutes.EDIT, action.item.id?.toString()]);
              }
            } else {
              route.push(...[PtUiAppRoutes.BUSINESS_MANAGEMENT, PtUiAppRoutes.AI_ASSESSMENTS]);
            }
            openBlank = true;
            break;
          case WorkspaceItemType.APP_ASSESSMENT:
          case WorkspaceItemType.APP_ASSESSMENT_VALIDATED:
            if (noSelected && existItemId) {
              if (isValidated) {
                route.push(...[PtUiAppRoutes.BUSINESS_MANAGEMENT, PtUiAppRoutes.APP_ASSESSMENTS, action.item.id?.toString()]);
              } else {
                route.push(...[PtUiAppRoutes.BUSINESS_MANAGEMENT, PtUiAppRoutes.APP_ASSESSMENTS, PtUiAppRoutes.EDIT, action.item.id?.toString()]);
              }
            } else {
              route.push(...[PtUiAppRoutes.BUSINESS_MANAGEMENT, PtUiAppRoutes.APP_ASSESSMENTS]);
            }
            openBlank = true;
            break;
          case WorkspaceItemType.COVID_ASSESSMENT:
          case WorkspaceItemType.COVID_ASSESSMENT_VALIDATED:
            if (noSelected && existItemId) {
              if (isValidated) {
                route.push(...[PtUiAppRoutes.BUSINESS_MANAGEMENT, PtUiAppRoutes.COVID_ASSESSMENTS, action.item.id?.toString()]);
              } else {
                route.push(...[PtUiAppRoutes.BUSINESS_MANAGEMENT, PtUiAppRoutes.COVID_ASSESSMENTS, PtUiAppRoutes.EDIT, action.item.id?.toString()]);
              }
            } else {
              route.push(...[PtUiAppRoutes.BUSINESS_MANAGEMENT, PtUiAppRoutes.COVID_ASSESSMENTS]);
            }
            openBlank = true;
            break;
          case WorkspaceItemType.FOLDER:
            if (existItemId) {
              route.push(...[PtUiAppRoutes.WORKSPACE, PtUiAppRoutes.RESOURCES, action.item.id?.toString()]);
            } else {
              route.push(...[PtUiAppRoutes.WORKSPACE, PtUiAppRoutes.RESOURCES]);
            }
            openBlank = false;
            break;
          case WorkspaceItemType.GLOBAL_MEASURE_CHECK:
          case WorkspaceItemType.GLOBAL_MEASURE_CHECK_VALIDATED:
            if (noSelected && existItemId) {
              if (isValidated) {
                route.push(...[PtUiAppRoutes.BUSINESS_MANAGEMENT, PtUiAppRoutes.GLOBAL_MEASURE_CHECK]);
              } else {
                route.push(...[PtUiAppRoutes.BUSINESS_MANAGEMENT, PtUiAppRoutes.GLOBAL_MEASURE_CHECK, PtUiAppRoutes.EDIT, action.item.id?.toString()]);
              }
            } else {
              route.push(...[PtUiAppRoutes.BUSINESS_MANAGEMENT, PtUiAppRoutes.GLOBAL_MEASURE_CHECK]);
            }
            openBlank = true;
            break;
          case WorkspaceItemType.TREATMENT_RESPONSIBLE:
          case WorkspaceItemType.TREATMENT_RESPONSIBLE_VALIDATED:
            if (noSelected && existItemId) {
              if (isValidated) {
                route.push(...[PtUiAppRoutes.BUSINESS_MANAGEMENT, PtUiAppRoutes.TREATMENTS_RESPONSIBLE, action.item.id?.toString()]);
              } else {
                route.push(...[PtUiAppRoutes.BUSINESS_MANAGEMENT, PtUiAppRoutes.TREATMENTS_RESPONSIBLE, PtUiAppRoutes.EDIT, action.item.id?.toString()]);
              }
            } else {
              route.push(...[PtUiAppRoutes.BUSINESS_MANAGEMENT, PtUiAppRoutes.TREATMENTS_RESPONSIBLE]);
            }
            openBlank = true;
            break;
          case WorkspaceItemType.TREATMENT_PROJECT:
          case WorkspaceItemType.TREATMENT_PROJECT_VALIDATED:
            if (noSelected && existItemId) {
              if (isValidated) {
                route.push(...[PtUiAppRoutes.BUSINESS_MANAGEMENT, PtUiAppRoutes.PROJECT_CARDS, action.item.id?.toString()]);
              } else {
                route.push(...[PtUiAppRoutes.BUSINESS_MANAGEMENT, PtUiAppRoutes.PROJECT_CARDS, PtUiAppRoutes.EDIT, action.item.id?.toString()]);
              }
            } else {
              route.push(...[PtUiAppRoutes.BUSINESS_MANAGEMENT, PtUiAppRoutes.PROJECT_CARDS]);
            }
            openBlank = true;
            break;
          case WorkspaceItemType.TREATMENT_HOLDER:
          case WorkspaceItemType.TREATMENT_HOLDER_VALIDATED:
            if (noSelected && existItemId) {
              if (isValidated) {
                route.push(...[PtUiAppRoutes.BUSINESS_MANAGEMENT, PtUiAppRoutes.TREATMENTS_HOLDER]);
                queryParams = { ...queryParams, id: action.item.id?.toString(), opened: true };
              } else {
                route.push(...[PtUiAppRoutes.BUSINESS_MANAGEMENT, PtUiAppRoutes.TREATMENTS_HOLDER, PtUiAppRoutes.EDIT, action.item.id?.toString()]);
              }
            } else {
              route.push(...[PtUiAppRoutes.BUSINESS_MANAGEMENT, PtUiAppRoutes.TREATMENTS_HOLDER]);
            }
            openBlank = true;
            break;
          case WorkspaceItemType.TREATMENT_SUB_RESPONSIBLE:
          case WorkspaceItemType.TREATMENT_SUB_RESPONSIBLE_VALIDATED:
            if (noSelected && existItemId) {
              if (isValidated) {
                route.push(...[PtUiAppRoutes.BUSINESS_MANAGEMENT, PtUiAppRoutes.TREATMENTS_SUB_RESPONSIBLE, action.item.id?.toString()]);
              } else {
                route.push(...[PtUiAppRoutes.BUSINESS_MANAGEMENT, PtUiAppRoutes.TREATMENTS_SUB_RESPONSIBLE, PtUiAppRoutes.EDIT, action.item.id?.toString()]);
              }
            } else {
              route.push(...[PtUiAppRoutes.BUSINESS_MANAGEMENT, PtUiAppRoutes.TREATMENTS_SUB_RESPONSIBLE]);
            }
            openBlank = true;
            break;
          case WorkspaceItemType.ATTACHMENT:
            route = [];
            queryParams = {};
            openBlank = false;
            break;
          default:
            break;
        }

        if (route?.length > 0) {
          this.store.dispatch(PtCoreActions.navigateTo({ route, queryParams, onBlankPage: openBlank }));
        }
      })
    );
  }, { dispatch: false });

  renameResource$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(WorkspaceActions.renameWorkspaceItem),
      tap((action: { item: WorkspaceItem }) => {
        this.dialogService.runRenameWorkspaceItemDialog(action.item).subscribe(response => {
          if (response) {
            const id = action.item.id;
            const request = {
              name: response?.name
            } as WorkspaceRenameRequest;

            switch (action.item.type) {
              case 'AI_ASSESSMENT':
                this.store.dispatch(AiAssessmentActions.renameAiAssessment({ id, request }));
                break;
              case 'APP_ASSESSMENT':
                this.store.dispatch(AppAssessmentActions.renameAppAssessment({ id, request }));
                break;
              case 'COVID_ASSESSMENT':
                this.store.dispatch(CovidAssessmentActions.renameCovidAssessment({ id, request }));
                break;
              case 'FOLDER':
                this.store.dispatch(FolderActions.renameFolder({ id, request }));
                break;
              case 'GLOBAL_MEASURE_CHECK':
                this.store.dispatch(RiskAssessmentActions.renameRiskAssessment({ id, request }));
                break;
              case 'TREATMENT_PROJECT':
              case 'TREATMENT_RESPONSIBLE':
              case 'TREATMENT_HOLDER':
              case 'TREATMENT_SUB_RESPONSIBLE':
                this.store.dispatch(TreatmentActions.renameTreatment({ id, request }));
                break;
              default:
            }
          }
        });
      }));
  }, { dispatch: false });

  createResource$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(WorkspaceActions.createWorkspaceItem),
      withLatestFrom(
        this.store.select(PtCoreSelectors.organization)
      ),
      tap(([action, org]) => {
        switch (action?.item.type) {
          case 'AI_ASSESSMENT':
            this.documentsDialogService.runCreateAiAssessmentDialog({
              openOnSuccess: false,
              dialogMode: 'CREATE',
              selectedFolderId: action?.item?.parentId
            });
            break;
          case 'APP_ASSESSMENT':
            this.documentsDialogService.runCreateAppAssessmentDialog({
              openOnSuccess: false,
              dialogMode: 'CREATE',
              selectedFolderId: action?.item?.parentId
            });
            break;
          case 'COVID_ASSESSMENT':
            this.documentsDialogService.runCreateCovidAssessmentDialog({
              openOnSuccess: false,
              dialogMode: 'CREATE',
              selectedFolderId: action?.item?.parentId
            });
            break;
          case 'FOLDER':
            this.dialogService.runCreateFolderDialog(
              action?.item?.parentId,
              null,
              false);
            break;
          case 'GLOBAL_MEASURE_CHECK':
            this.documentsDialogService.runCreateRiskAssessmentDialog({
              openOnSuccess: false,
              dialogMode: 'CREATE',
              selectedFolderId: action?.item?.parentId
            } as CreateRiskAssessmentDialogDto);
            break;
          case 'ATTACHMENT':
            this.runImportAttachment({
              title: this.translate.stream('TITLE.ADD_FILE'),
              hideWorkspaceTab: true,
              selectedFolderId: action?.item?.parentId
            } as AttachFileDialog);
            break;
          case 'TREATMENT_SUB_RESPONSIBLE':
          case 'TREATMENT_HOLDER':
          case 'TREATMENT_PROJECT':
          case 'TREATMENT_RESPONSIBLE':
            this.documentsDialogService.runCreateTreatmentDialog({
              openOnSuccess: false,
              dialogMode: 'CREATE',
              treatmentType: TreatmentTypesConverter.ofWorkspaceItemType(action?.item.type),
              selectedFolderId: action?.item?.parentId,
            }).subscribe();
        }
      }));
  }, { dispatch: false });

  runImportAttachment = (attachFileDialog: AttachFileDialog) => {
    const modalRef = this.modalService.open(AttachFileDialogComponent, {
      centered: true,
      backdrop: 'static',
      size: 'xl'
    });
    modalRef.componentInstance.title = attachFileDialog.title;
    modalRef.componentInstance.hideWorkspaceTab = attachFileDialog.hideWorkspaceTab;
    modalRef.componentInstance.selectedFolderId = attachFileDialog.selectedFolderId;
    return from(modalRef.result);
  };

  downloadWorkspaceItem$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(WorkspaceActions.downloadWorkspaceItem),
      tap((action: { item: WorkspaceItem }) => {
        switch (action.item.type) {
          case WorkspaceItemType.FOLDER:
            break;
          case WorkspaceItemType.ATTACHMENT:
            this.attachmentsService.getAttachmentById(action.item.id).subscribe(response => {
              if (response) {
                this.attachmentsService.downloadAttachment(
                  response,
                  response.attachmentType
                );
              }
              this.store.dispatch(downloadWorkspaceItemEnd());
            }, () => this.store.dispatch(downloadWorkspaceItemEnd()));
            break;

          case WorkspaceItemType.GLOBAL_MEASURE_CHECK_VALIDATED:
          case WorkspaceItemType.TREATMENT_HOLDER_VALIDATED:
          case WorkspaceItemType.TREATMENT_PROJECT_VALIDATED:
          case WorkspaceItemType.TREATMENT_RESPONSIBLE_VALIDATED:
          case WorkspaceItemType.TREATMENT_SUB_RESPONSIBLE_VALIDATED:
          case WorkspaceItemType.AI_ASSESSMENT_VALIDATED:
          case WorkspaceItemType.APP_ASSESSMENT_VALIDATED:
          case WorkspaceItemType.COVID_ASSESSMENT_VALIDATED:
          case WorkspaceItemType.GLOBAL_MEASURE_CHECK_SNAPSHOT:
          case WorkspaceItemType.TREATMENT_HOLDER_SNAPSHOT:
          case WorkspaceItemType.TREATMENT_PROJECT_SNAPSHOT:
          case WorkspaceItemType.TREATMENT_RESPONSIBLE_SNAPSHOT:
          case WorkspaceItemType.TREATMENT_SUB_RESPONSIBLE_SNAPSHOT:
          case WorkspaceItemType.AI_ASSESSMENT_SNAPSHOT:
          case WorkspaceItemType.APP_ASSESSMENT_SNAPSHOT:
          case WorkspaceItemType.COVID_ASSESSMENT_SNAPSHOT: {
            const service = this.downloadService.getWorkspaceItemTypeService(action.item.type);
            service?.downloadValidated(action.item.id, action.item.name).subscribe(
              (response: DownloadResponse) => {
                const dialog = this.widgetDialogService.runPdfPreview(of(action.item.name), response);
                this.store.dispatch(downloadWorkspaceItemEnd());
                dialog.subscribe();
              }, () => {
                this.store.dispatch(downloadWorkspaceItemEnd());
              }
            );
            break;
          }
          case WorkspaceItemType.COVID_ASSESSMENT:
          case WorkspaceItemType.GLOBAL_MEASURE_CHECK:
          case WorkspaceItemType.AI_ASSESSMENT:
          case WorkspaceItemType.APP_ASSESSMENT:
          case WorkspaceItemType.TREATMENT_HOLDER:
          case WorkspaceItemType.TREATMENT_PROJECT:
          case WorkspaceItemType.TREATMENT_RESPONSIBLE:
          case WorkspaceItemType.TREATMENT_SUB_RESPONSIBLE:
          case WorkspaceItemType.DATA_BREACH:
          default:
            this.store.dispatch(PtCoreActions.documentDirectDownload({
              docId: action.item.id,
              resourceType: action.item.type as WorkspaceItemType
            }));
        }
      }));
  }, { dispatch: false });

  documentDirectDownload$ = createEffect(() => this.actions$.pipe(
    ofType(PtCoreActions.documentDirectDownload),
    map((action) => {
        this.downloadService.documentDirectDownload(action.docId, action.resourceType);
        setTimeout(() => {
          this.store.dispatch(downloadWorkspaceItemEnd());
        }, 2000);
      }
    )
  ), { dispatch: false });


  importResource$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AttachmentActions.importAttachmentWithDialog),
      tap((action: { parentId?: number }) => {
        const title = this.translate.stream('TITLE.ADD_FILE');
        this.dialogService.runAttachDialog(
          title,
          true,
          action.parentId
        ).subscribe(() => {
          this.store.dispatch(AttachmentActions.importAttachmentSuccess({ parentId: action.parentId }));
        }, (error) => {
          this.store.dispatch(AttachmentActions.importAttachmentFailed({ error }));
        });
      })
    );
  }, { dispatch: false });

}
